// src/components/AudioContextKeepAlive.jsx
import { useEffect, useRef } from 'react';

/**
 * AudioContextKeepAlive Component
 * 
 * Prevents browser tab throttling by maintaining an active AudioContext.
 * When a tab has active audio, browsers treat it as foreground even when hidden.
 * This ensures LiveKit connections and video processing continue uninterrupted.
 */
const AudioContextKeepAlive = () => {
  const audioContextRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const keepAliveIntervalRef = useRef(null);

  useEffect(() => {
    // Initialize audio context only once
    if (audioContextRef.current) return;

    try {
      // Create audio context with low latency hint
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      const context = new AudioContextClass({
        latencyHint: 'interactive',
      });
      
      audioContextRef.current = context;

      // Create a silent audio buffer (1 sample of silence)
      const buffer = context.createBuffer(
        1,     // channels
        1,     // length in samples  
        22050  // sample rate
      );
      
      // Create and configure a looping silent source
      const source = context.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      source.connect(context.destination);
      
      // Start playing the silent audio
      source.start(0);
      sourceNodeRef.current = source;

      console.log('[AudioKeepAlive] Initialized - tab throttling prevention active');

      // Chrome suspends audio contexts after user interaction
      // Resume it periodically if needed
      const resumeContext = () => {
        if (context.state === 'suspended') {
          context.resume()
            .then(() => console.log('[AudioKeepAlive] Context resumed'))
            .catch(err => console.warn('[AudioKeepAlive] Resume failed:', err));
        }
      };

      // Check every 5 seconds if context needs resuming
      keepAliveIntervalRef.current = setInterval(resumeContext, 5000);

      // Also resume on any user interaction
      const handleUserInteraction = () => {
        if (context.state === 'suspended') {
          resumeContext();
        }
      };

      // Listen for user interactions to resume suspended context
      document.addEventListener('click', handleUserInteraction, { once: true });
      document.addEventListener('keydown', handleUserInteraction, { once: true });

      // Handle page visibility changes
      const handleVisibilityChange = () => {
        if (!document.hidden && context.state === 'suspended') {
          resumeContext();
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Cleanup function
      return () => {
        console.log('[AudioKeepAlive] Cleaning up...');
        
        // Clear interval
        if (keepAliveIntervalRef.current) {
          clearInterval(keepAliveIntervalRef.current);
          keepAliveIntervalRef.current = null;
        }

        // Stop source
        if (sourceNodeRef.current) {
          try {
            sourceNodeRef.current.stop();
            sourceNodeRef.current.disconnect();
          } catch (e) {
            // Source might already be stopped
          }
          sourceNodeRef.current = null;
        }

        // Close audio context
        if (audioContextRef.current) {
          audioContextRef.current.close()
            .then(() => console.log('[AudioKeepAlive] Context closed'))
            .catch(err => console.warn('[AudioKeepAlive] Close failed:', err));
          audioContextRef.current = null;
        }

        // Remove event listeners
        document.removeEventListener('click', handleUserInteraction);
        document.removeEventListener('keydown', handleUserInteraction);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    } catch (error) {
      console.error('[AudioKeepAlive] Failed to initialize:', error);
      
      // Fallback: try alternative anti-throttling method
      // Use a simple interval-based approach as backup
      const fallbackInterval = setInterval(() => {
        // This keeps JavaScript timer active
        const now = Date.now();
      }, 1000);

      return () => {
        clearInterval(fallbackInterval);
      };
    }
  }, []); // Empty deps - run once on mount

  // This component renders nothing
  return null;
};

export default AudioContextKeepAlive;